'use client';

import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Section } from '@/components/ui/Section';
import { Button } from '@/components/ui/Button';
import { RichText } from '@/components/ui/RichText';
import styles from './SolutionsDeepDive.module.css';
import type { Locale } from '@/lib/i18n';

interface SolutionImage {
  src: string;
  alt: string;
}

interface Solution {
  id: 'kiosk' | 'qr' | 'tablet' | 'webshop' | 'ayce';
  hook: string;
  usps: string[];
  images: SolutionImage[];
  cta: {
    text: string;
    url: string;
    ariaLabel: string;
  };
}

export interface SolutionsDeepDiveProps {
  badge: string;
  title: string;
  subtitle: string;
  solutions: Solution[];
  footerCta: {
    text: string;
    subtitle: string;
    url: string;
  };
  locale: Locale;
}

/**
 * SolutionsDeepDive Component
 * Displays detailed information about the core ordering solutions with alternating layout,
 * image sliders, and SEO-optimized content
 */
export default function SolutionsDeepDive({
  badge,
  title,
  subtitle,
  solutions,
  footerCta,
  locale,
}: SolutionsDeepDiveProps) {
  const [activeSlides, setActiveSlides] = useState<Record<string, number>>({});
  const [isMounted, setIsMounted] = useState(false);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const sliderIntervals = useRef<Record<string, NodeJS.Timeout>>({});

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Auto-play sliders with 4 second intervals
  useEffect(() => {
    if (!isMounted) return;

    solutions.forEach((solution) => {
      sliderIntervals.current[solution.id] = setInterval(() => {
        setActiveSlides((prev) => ({
          ...prev,
          [solution.id]: ((prev[solution.id] ?? 0) + 1) % solution.images.length,
        }));
      }, 4000);
    });

    return () => {
      Object.values(sliderIntervals.current).forEach(clearInterval);
    };
  }, [isMounted, solutions]);

  // Pause slider on hover (accessibility)
  const handleMouseEnter = (solutionId: string) => {
    if (sliderIntervals.current[solutionId]) {
      clearInterval(sliderIntervals.current[solutionId]);
    }
  };

  const handleMouseLeave = (solutionId: string, imagesLength: number) => {
    sliderIntervals.current[solutionId] = setInterval(() => {
      setActiveSlides((prev) => ({
        ...prev,
        [solutionId]: (prev[solutionId] + 1) % imagesLength,
      }));
    }, 4000);
  };

  // Handle dot click
  const handleDotClick = (solutionId: string, index: number) => {
    setActiveSlides((prev) => ({
      ...prev,
      [solutionId]: index,
    }));
  };

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!isMounted) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.visible);
          }
        });
      },
      { rootMargin: '50px', threshold: 0.05 }
    );

    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [isMounted]);

  return (
    <Section background="secondary" paddingY="xl">
      {/* Background Image Layer */}
      <div className={styles.backgroundImage}>
        <Image
          src="/images/backgrounds/solutions-botanical-hq.webp"
          alt=""
          fill
          sizes="100vw"
          style={{ objectFit: 'cover', objectPosition: 'center' }}
          quality={80}
          priority={false}
          loading="lazy"
        />
      </div>

      {/* Dark Overlay */}
      <div className={styles.backgroundOverlay} />

      {/* Content */}
      <div className={styles.contentWrapper}>
        <div className={styles.deepDive}>
        {/* Header */}
        <div className={styles.header}>
          <span className={styles.badge}>{badge}</span>
          <h2 className={styles.title}>{title}</h2>
          <RichText className={styles.subtitle} text={subtitle} />
        </div>

        {/* Solution Blocks */}
        <div className={styles.solutions}>
          {solutions.map((solution, index) => {
            const isReversed = index % 2 === 1;
            const activeSlide = activeSlides[solution.id] ?? 0;

            return (
              <div
                key={solution.id}
                ref={(el) => {
                  sectionRefs.current[index] = el;
                }}
                className={`${styles.solutionBlock} ${
                  isReversed ? styles.reversed : ''
                }`}
              >
                {/* Image Slider */}
                <div
                  className={styles.sliderWrapper}
                  onMouseEnter={() => handleMouseEnter(solution.id)}
                  onMouseLeave={() =>
                    handleMouseLeave(solution.id, solution.images.length)
                  }
                  aria-label={({
                    en: `${solution.hook} images`,
                    nl: `${solution.hook} afbeeldingen`,
                    de: `${solution.hook} Bilder`,
                    sv: `${solution.hook} bilder`,
                  } as Record<string, string>)[locale] ?? `${solution.hook} images`}
                >
                  <div className={styles.slider}>
                    {solution.images.map((image, imgIndex) => (
                      <div
                        key={imgIndex}
                        className={`${styles.sliderImage} ${
                          imgIndex === activeSlide ? styles.active : ''
                        }`}
                      >
                        <Image
                          src={image.src}
                          alt={image.alt}
                          fill
                          style={{ objectFit: 'contain', objectPosition: 'center' }}
                          sizes="(max-width: 768px) 100vw, 50vw"
                          quality={85}
                          priority={index === 0 && imgIndex === 0}
                          loading={
                            index === 0 && imgIndex === 0 ? 'eager' : 'lazy'
                          }
                        />
                      </div>
                    ))}
                  </div>

                  {/* Slider Dots */}
                  <div className={styles.sliderDots}>
                    {solution.images.map((_, dotIndex) => (
                      <button
                        key={dotIndex}
                        className={`${styles.dot} ${
                          dotIndex === activeSlide ? styles.dotActive : ''
                        }`}
                        onClick={() => handleDotClick(solution.id, dotIndex)}
                        aria-label={({
                          en: `Go to image ${dotIndex + 1}`,
                          nl: `Ga naar afbeelding ${dotIndex + 1}`,
                          de: `Gehe zu Bild ${dotIndex + 1}`,
                          sv: `Gå till bild ${dotIndex + 1}`,
                        } as Record<string, string>)[locale] ?? `Go to image ${dotIndex + 1}`}
                      />
                    ))}
                  </div>
                </div>

                {/* Text Content */}
                <div className={styles.textContent}>
                  <h3 className={styles.hook}>{solution.hook}</h3>

                  <ul className={styles.usps}>
                    {solution.usps.map((usp, uspIndex) => (
                      <li key={uspIndex} className={styles.usp}>
                        {usp}
                      </li>
                    ))}
                  </ul>

                  <div className={styles.ctaWrapper}>
                    <Link href={solution.cta.url}>
                      <Button
                        variant="primary"
                        size="md"
                        aria-label={solution.cta.ariaLabel}
                        data-cta-id={`cta_solution_${solution.id}`}
                        data-cta-position="solutions_deep_dive"
                      >
                        {solution.cta.text}
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer CTA */}
        <div className={styles.footerCta}>
          <p className={styles.footerCtaSubtitle}>{footerCta.subtitle}</p>
          <Link href={footerCta.url}>
            <Button
              variant="primary"
              size="lg"
              data-cta-id="cta_solutions_demo"
              data-cta-position="solutions_deep_dive_footer"
            >
              {footerCta.text}
            </Button>
          </Link>
        </div>
          </div>
        </div>
    </Section>
  );
}
